/**
 * muzie.live migration support — visible ONLY to the muzie.live tenant
 * owner (server-enforced; the nav item is additionally hidden for everyone
 * else). Answers "did this user's migration work?" from the durable
 * migration_results records plus live job state, and can requeue a failed
 * job (the runner is kicked server-side).
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApi } from '../../../hooks/useApi';

const STATUS_COLOR = { completed: 'success', running: 'info', queued: 'warning', failed: 'error' };

function ResultDetail({ result }) {
  if (!result) return <Typography variant="body2">No results record for this run.</Typography>;
  return (
    <Box sx={{ p: 1 }}>
      {/* Did this run move MONEY? The billing arm flag is live (it takes
          effect on the next job), so two runs minutes apart can differ —
          the per-run record is the only honest answer. */}
      <Chip
        size="small"
        sx={{ mb: 1 }}
        color={result.billingArmed ? 'error' : 'default'}
        variant={result.billingArmed ? 'filled' : 'outlined'}
        label={result.billingArmed ? 'billing ARMED (real Stripe writes)' : 'billing preview only'}
      />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Domain</TableCell>
            <TableCell align="right">Legacy</TableCell>
            <TableCell align="right">Migrated</TableCell>
            <TableCell align="right">Skipped</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(result.counts || []).map((c) => (
            <TableRow key={c.domain}>
              <TableCell>{c.domain}</TableCell>
              <TableCell align="right">{c.legacy}</TableCell>
              <TableCell align="right">{c.transformed}</TableCell>
              <TableCell align="right">{c.skipped}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {(result.warnings || []).length > 0 && (
        <Box sx={{ mt: 1, maxHeight: 220, overflowY: 'auto' }}>
          <Typography variant="subtitle2">Warnings ({result.warnings.length})</Typography>
          {result.warnings.map((w, i) => (
            <Typography key={i} variant="caption" component="div" sx={{ color: 'text.secondary' }}>
              {w}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function MigrationsSupport() {
  // useApi() returns a NEW object every render — depending on it from a
  // useCallback re-armed the mount effect on every state change, firing an
  // unbounded request loop (429s in production, and the error path kept the
  // loop alive). The ref keeps `load` referentially stable for the mount
  // effect while always calling the current client.
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(
    async (query) => {
      setBusy(true);
      setError(null);
      try {
        const res = await apiRef.current.get(`/api/migrations?q=${encodeURIComponent(query ?? '')}`);
        setData(res);
      } catch (err) {
        setError(err.message || 'Lookup failed');
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  useEffect(() => {
    load('');
  }, [load]);

  const requeue = async (id) => {
    setBusy(true);
    try {
      await apiRef.current.post(`/api/migrations/jobs/${id}/requeue`, {});
      await load(q);
    } catch (err) {
      setError(err.message || 'Requeue failed');
      setBusy(false);
    }
  };

  const [arm, setArm] = useState(null);
  const [armDialogOpen, setArmDialogOpen] = useState(false);
  const [armPhrase, setArmPhrase] = useState('');
  const [armBusy, setArmBusy] = useState(false);

  const loadArm = useCallback(async () => {
    try {
      setArm(await apiRef.current.get('/api/migrations/billing-arm'));
    } catch {
      setArm({ armed: false, error: 'could not read the flag' });
    }
  }, []);
  useEffect(() => {
    void loadArm();
  }, [loadArm]);

  const flipArm = async (armed, confirmation) => {
    setArmBusy(true);
    setError(null);
    try {
      const next = await apiRef.current.post('/api/migrations/billing-arm', {
        armed,
        ...(confirmation ? { confirmation } : {}),
      });
      setArm(next);
      setArmDialogOpen(false);
      setArmPhrase('');
    } catch (err) {
      setError(err.message || 'Could not change the billing arm state');
    } finally {
      setArmBusy(false);
    }
  };

  const [declineEmail, setDeclineEmail] = useState('');
  const [declineMsg, setDeclineMsg] = useState(null);
  const clearDecline = async () => {
    setBusy(true);
    setDeclineMsg(null);
    setError(null);
    try {
      const r = await apiRef.current.post('/api/migrations/clear-decline', {
        email: declineEmail.trim().toLowerCase(),
      });
      setDeclineMsg(
        r.wasDeclined
          ? 'Decline cleared — the user can run /migrate again.'
          : 'That address was not declined (nothing to clear).',
      );
      setDeclineEmail('');
    } catch (err) {
      setError(err.message || 'Clear decline failed');
    } finally {
      setBusy(false);
    }
  };

  const resultFor = (runId) => (data?.results || []).find((r) => r.runId === runId);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        muzie.live Migrations
      </Typography>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by email, teacher code, or run id"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(q)}
        />
        <Button variant="contained" onClick={() => load(q)} disabled={busy}>
          Search
        </Button>
        <IconButton
          onClick={() => load(q)}
          disabled={busy}
          title="Refresh"
          aria-label="Refresh"
        >
          {busy ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {declineMsg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDeclineMsg(null)}>{declineMsg}</Alert>}
      <Dialog open={armDialogOpen} onClose={() => !armBusy && setArmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Arm billing writes?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Migrations will then make REAL Stripe writes: a paying teacher&apos;s legacy
            subscription is cancelled and recreated on the live account. This affects an
            account carrying ~181 real subscriptions. Takes effect on the next job.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type <strong>{arm?.confirmationPhrase ?? 'ARM BILLING'}</strong> to confirm:
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={armPhrase}
            onChange={(e) => setArmPhrase(e.target.value)}
            disabled={armBusy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArmDialogOpen(false)} disabled={armBusy}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={armBusy || armPhrase !== (arm?.confirmationPhrase ?? 'ARM BILLING')}
            onClick={() => void flipArm(true, armPhrase)}
          >
            {armBusy ? <CircularProgress size={20} color="inherit" /> : 'Arm billing'}
          </Button>
        </DialogActions>
      </Dialog>
      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          border: 1,
          borderColor: arm?.armed ? 'error.main' : 'divider',
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          Billing writes
        </Typography>
        <Chip
          size="small"
          color={arm?.armed ? 'error' : 'default'}
          variant={arm?.armed ? 'filled' : 'outlined'}
          label={arm === null ? 'checking…' : arm.armed ? 'ARMED — real Stripe writes' : 'preview only'}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
          {arm?.error
            ? arm.error
            : 'Takes effect on the NEXT job — the runner reads this flag fresh on every spawn.'}
        </Typography>
        {arm?.armed ? (
          // Disarming is deliberately one click: a kill switch must never be
          // awkward to pull.
          <Button color="inherit" variant="outlined" disabled={armBusy} onClick={() => void flipArm(false)}>
            Disarm
          </Button>
        ) : (
          <Button color="error" variant="outlined" disabled={armBusy} onClick={() => setArmDialogOpen(true)}>
            Arm billing…
          </Button>
        )}
      </Paper>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          Accidental decline?
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="User email — re-opens self-serve /migrate for them"
          value={declineEmail}
          onChange={(e) => setDeclineEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && declineEmail.trim() && clearDecline()}
        />
        <Button variant="outlined" onClick={clearDecline} disabled={busy || !declineEmail.trim()}>
          Clear decline
        </Button>
      </Paper>
      {busy && !data && <CircularProgress />}
      {data && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>User</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Domains</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Run</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.jobs || []).map((j) => (
                <Fragment key={j.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setOpen(open === j.id ? null : j.id)}>
                        <ExpandMoreIcon
                          sx={{ transform: open === j.id ? 'rotate(180deg)' : 'none' }}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell>{j.targetEmail}</TableCell>
                    <TableCell>{j.teacherCode}</TableCell>
                    <TableCell>
                      <Chip size="small" label={j.status} color={STATUS_COLOR[j.status] || 'default'} />
                      {j.billingAttention && (
                        <Chip
                          size="small"
                          label="billing"
                          color="warning"
                          sx={{ ml: 0.5 }}
                          title={j.billingAttention}
                        />
                      )}
                    </TableCell>
                    <TableCell>{(j.domains || []).join(', ')}</TableCell>
                    <TableCell>{j.createdAt ? new Date(j.createdAt).toLocaleString() : '—'}</TableCell>
                    <TableCell>{j.runId || '—'}</TableCell>
                    <TableCell>
                      {j.status === 'failed' && (
                        <Button size="small" startIcon={<ReplayIcon />} onClick={() => requeue(j.id)}>
                          Requeue
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ p: 0, border: open === j.id ? undefined : 0 }}>
                      <Collapse in={open === j.id} unmountOnExit>
                        {j.billingAttention && (
                          <Alert severity="warning" sx={{ m: 1, whiteSpace: 'pre-wrap' }}>
                            {'Billing needs an operator: ' + j.billingAttention +
                              '\n(The data phases completed — the user sees success. ' +
                              'Resolve in Stripe, then requeue if a billing re-run is needed.)'}
                          </Alert>
                        )}
                        {j.status === 'failed' && j.error && (
                          <Alert severity="error" sx={{ m: 1, whiteSpace: 'pre-wrap' }}>
                            {j.error}
                          </Alert>
                        )}
                        <ResultDetail result={resultFor(j.runId)} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
              {(data.jobs || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
                      No migrations found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
